import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { LNB_MENU } from "@/types/lnb";

import LNBParentButton from "./ParentButton";

import { cn } from "@/libs/utils";
import { queryClient } from "@/libs/react-query/queryClient";

import useUserStore from "@/stores/User";
import useBidStore from "@/stores/Bid";

import { GetCurrentUser } from "@/services/Setting";
import { DoLogout } from "@/services/Login";
import {
  clearAllCookies,
  clearBrowserStorage,
  broadcastLogout,
} from "@/utils/cookieUtils";

import CommonImg from "@/components/Common/Img";
import { Button } from "@/components/ui/button";

import Logo from "@/assets/images/thermo-fisher-logo.png";
import DashboardIcon from "@/assets/images/LNB/dashboard-icon.png";
import DashboardIconActive from "@/assets/images/LNB/dashboard-icon-active.png";
import PastIcon from "@/assets/images/LNB/past-icon.png";
import PastIconActive from "@/assets/images/LNB/past-icon-active.png";
import BidIcon from "@/assets/images/LNB/bid-icon.png";
import BidIconActive from "@/assets/images/LNB/bid-icon-active.png";
import DomesticIcon from "@/assets/images/LNB/domestic-icon.png";
import DomesticIconActive from "@/assets/images/LNB/domestic-icon-active.png";
import FavoriteIcon from "@/assets/images/LNB/favorite-icon.png";
import FavoriteIconActive from "@/assets/images/LNB/favorite-icon-active.png";
import WorkIcon from "@/assets/images/LNB/work-icon.png";
import WorkIconActive from "@/assets/images/LNB/work-icon-active.png";
import SettingIcon from "@/assets/images/LNB/setting-icon.png";
import SettingIconActive from "@/assets/images/LNB/setting-icon-active.png";
import TempProfile from "@/assets/images/LNB/temp-profile.jpg";
import LogoutIcon from "@/assets/images/LNB/logout-icon.png";

import NotificationPopover from "../NotificationPopover";

interface LNBProps {
  isOpen: boolean;
  onToggle: () => void;
}

export default function LNB({ isOpen, onToggle }: LNBProps) {
  const location = useLocation();
  const navigate = useNavigate();

  const storedUser = useUserStore((state) => state.user);
  const setStoredUser = useUserStore((state) => state.setUser);

  const menuList: LNB_MENU[] = [
    {
      id: "dashboard",
      src: DashboardIcon,
      activeSrc: DashboardIconActive,
      label: "대시보드",
    },
    {
      id: "bid",
      src: BidIcon,
      activeSrc: BidIconActive,
      label: "입찰검색",
      children: [
        {
          id: "domestic/list",
          src: DomesticIcon,
          activeSrc: DomesticIconActive,
          label: "국내입찰",
        },
        {
          id: "favorite/list",
          src: FavoriteIcon,
          activeSrc: FavoriteIconActive,
          label: "관심공고",
        },
      ],
    },
    {
      id: "past",
      src: PastIcon,
      activeSrc: PastIconActive,
      label: "과거사업",
    },
    {
      id: "management",
      src: WorkIcon,
      activeSrc: WorkIconActive,
      label: "입찰관리",
      defaultPath: "/management/projects",
    },
    {
      id: "approval",
      src: WorkIcon,
      activeSrc: WorkIconActive,
      label: "승인관리",
      defaultPath: "/approvals/requests",
    },
  ];
  const settingMenu: LNB_MENU = {
    id: "setting",
    src: SettingIcon,
    activeSrc: SettingIconActive,
    label: "설정",
  };

  const getCurrentUser = async () => {
    try {
      const result = await GetCurrentUser();
      setStoredUser(result.data.pods[0].content);
    } catch (e) {
    } finally {
    }
  };

  const handleClickLogout = async () => {
    try {
      await DoLogout();
    } catch (error) {
      console.error("로그아웃 API 실패 : ", error);
    } finally {
      // 쿠키, 스토리지, 캐시, 스토어 초기화 및 로그아웃 이벤트 발생
      clearAllCookies();
      clearBrowserStorage();
      queryClient.clear();
      useUserStore.getState().reset();
      useBidStore.getState().reset();
      broadcastLogout();
      navigate("/login", { replace: true });
    }
  };

  const onLoad = () => {
    getCurrentUser();
  };

  useEffect(() => {
    onLoad();
  }, []);

  return (
    <>
      <div
        className={cn(
          "h-full flex flex-col justify-between p-[40px] border-r-[1px] border-r-gray-D transition-all duration-300 ease-in-out",
          isOpen ? "min-w-[300px] w-[300px]" : "min-w-0 w-0 p-0 overflow-hidden"
        )}
      >
        <div className="flex flex-col gap-[80px]">
          <div className="flex justify-between items-center">
            <CommonImg
              src={Logo}
              alt="thermo fisher logo"
              className="h-[32px] w-[143px]"
            />
            <NotificationPopover />
          </div>

          <div className="flex flex-col">
            <div>
              {menuList.map((menu) => (
                <LNBParentButton
                  key={menu.id}
                  path={location.pathname}
                  {...menu}
                  onClick={(path) => {
                    const targetPath = menu.defaultPath ?? path;
                    navigate(targetPath);
                  }}
                />
              ))}
            </div>
          </div>
        </div>

        <div>
          <LNBParentButton
            {...settingMenu}
            path={location.pathname}
            onClick={(path) => {
              navigate(path);
            }}
          />
          <div className="flex justify-between items-center pt-[40px]">
            <CommonImg
              src={storedUser?.image_url ?? TempProfile}
              alt="Profile"
              className="w-[80px] h-[80px] rounded-[50px]"
            />
            <div>
              <div className="font-bold text-lg text-black flex gap-[8px]">
                <span>
                  {storedUser?.last_name}
                  {storedUser?.first_name}
                </span>
                <span>{storedUser?.position}</span>
              </div>
              <div className="flex gap-[10px] items-center">
                <div
                  className={cn(
                    "px-[16px] py-[4px] rounded-[16px] bg-dark-red text-sm text-white font-bold",
                    { "w-[60px] bg-white": !storedUser?.groups?.[0] }
                  )}
                >
                  {storedUser?.groups?.[0]?.group_name}
                </div>
                <CommonImg
                  className="size-[16px] cursor-pointer"
                  src={LogoutIcon}
                  alt="Logout"
                  onClick={handleClickLogout}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 토글 버튼 */}
      <Button
        onClick={onToggle}
        variant="outline"
        className={cn(
          "fixed top-4 z-50 shadow-md hover:shadow-lg transition-all duration-200 w-8 h-8 p-1",
          isOpen ? "left-[310px]" : "left-4"
        )}
        aria-label={isOpen ? "사이드바 닫기" : "사이드바 열기"}
      >
        <svg
          className="w-4 h-4 text-black"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          {isOpen ? (
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M11 19l-7-7 7-7m8 14l-7-7 7-7"
            />
          ) : (
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 5l7 7-7 7M5 5l7 7-7 7"
            />
          )}
        </svg>
      </Button>
    </>
  );
}
